import {
  OrderFormat,
  SalesLocationType,
  CheckoutSalesLocation,
  OutletListItem,
  ServiceTime,
  CheckoutDelivery,
  CheckoutSummary,
  PaymentProviderResult,
  CheckoutStatus,
  TicketStatus,
  TicketItemStatus,
  PreOrderStatus,
  AttendeePreOrderStatus,
  BoxPickUpStation,
  PreOrderCancelInfo,
  Amount,
  CheckoutDiscount,
  CheckoutMetaDataItem,
  CheckoutDeliveryStatus,
  CheckoutDeliveryProvider,
  IDNameItem,
  CheckoutDetails,
  OnlinePaymentProvider,
  OutletDetails,
  OrderSummaryOrderItem,
  OrderSummaryTicketItemProduct,
  VariantType,
} from '@ancon/wildcat-types'
import { CheckOrderSummaryItem } from '@ancon/wildcat-utils/order/types'
import { SerializedGenericError } from '@ancon/wildcat-utils/error/types'
import { CheckoutRequestItem } from '@ancon/wildcat-utils/checkout/types'

import { AdyenPaymentMethodsResponse } from '../adyen/types'
import { AddTipState } from '../app/components/addTipModal/types'

export type CheckoutFilters = {
  orderFormat: OrderFormat | null
  serviceTime: ServiceTime
  salesLocation: CheckoutSalesLocation | null
  delivery: CheckoutDelivery | null
  section: BoxPickUpStation | null
}

export type CheckoutRequestedItemDetails = {
  variantId: string
  productId: string
  /** Already resolved through the hasVariants rule - ready to display. */
  name: string
  variantType?: VariantType
}

export type CheckoutOutOfStockAdditionalData = {
  stockProductId: string
  productId: string
  variantId: string
  count: number
  reorderCount: number
}

export type CheckoutReducerState = {
  checkoutFilters: CheckoutFilters

  currentCheckoutOutletId: string | null
  currentCheckoutOutletListItem?: OutletListItem | null

  currentCheckoutId?: string | null
  currentCheckoutDetails: CheckoutDetails | null
  currentCheckoutSelectedCheckoutItemId?: string | null
  currentCheckoutDetailsFetchPending: boolean
  currentCheckoutPaymentPending: boolean
  checkoutOutletListItemFetchPending: boolean

  checkoutSummaryFetchPending: boolean
  checkoutSummary: CheckoutSummary | null
  checkoutSummaryOutletListItem: OutletListItem | null

  checkoutWayOfPaymentsFetchPending: boolean
  adyenPaymentMethodsFetchPending: boolean
  hasAdyenPaymentMethods: boolean

  isFetchingCheckoutAdyenPaymentMethods: boolean
  checkoutAdyenPaymentMethods: AdyenPaymentMethodsResponse | null
  checkoutAdyenPaymentMethodsError: SerializedGenericError | null
  checkoutAdyenPaymentAttemptCount: number

  /** Adds in flight by request id, counted in the cart rail before core answers */
  checkoutAddItemRequests: Record<string, CheckoutAddItemRequest>
  checkoutUpdatePending: boolean
  checkoutApplyDiscountPending: boolean
  checkoutUpdateItemQuantityPending: boolean
  checkoutDeletePending: boolean
  checkoutUpdateItemPending: boolean
  checkoutValidatePending: boolean
  checkoutCancelPending: boolean

  isMinimumOrderModalVisible: boolean
  isDiscountModalVisible: boolean
  isCustomerInfoModalVisible: boolean
  isVisibleDeleteOrderModal: boolean
  isVisibleBoxPickupScheduleOrderModal: boolean

  capacityConflictModalState: CapacityConflictModalState | null

  /**
   * Display details for items the user has asked to put in the cart, keyed by
   * variant id. The client only ever sends variant ids, so this is what lets an
   * error that names product ids be shown with real product names - including
   * when the request that failed was the one that would have created the
   * checkout, so there is no checkout to read names from.
   */
  checkoutRequestedItemDetails: Record<string, CheckoutRequestedItemDetails>

  checkoutStockErrorAdditionalData: CheckoutOutOfStockAdditionalData[] | null
  checkoutInvalidDataErrorAdditionalData:
    | CheckoutInvalidDataErrorAdditionalData[]
    | null

  checkoutStatusLastUpdatedAt: number

  checkoutTip: AddTipState

  /** Track the system product checkout warning modal visibility */
  checkoutSystemProductWarning: CheckoutSystemProductWarningType
}

type CheckoutParamsBase = {
  outletId: string
  checkoutId: string
  checkoutItemId: string
}

export type CheckoutAPIParams<K extends keyof CheckoutParamsBase> = {
  [key in K]: CheckoutParamsBase[K]
}

export enum CheckoutErrorContextType {
  AddCheckoutItem,
  CreateCheckout,
  UpdateCheckout,
  ChangeCheckoutItemQuantity,
}

export type CreateCheckoutRequestBody = {
  orderFormat: OrderFormat
  serviceTime: ServiceTime
  items: CheckoutRequestItem[]
  delivery?: CheckoutDelivery | null
  salesLocation?: {
    id?: string
    type: SalesLocationType
  }
  instructions?: string | null
  customerId?: string
  companyId: string | null
  metaData?: string | null
}

type AttendeeItem = {
  attendeeId: string | null
  items: CheckoutRequestItem[]
}

export type UpdateCheckoutRequestBody = Omit<
  CreateCheckoutRequestBody,
  'items'
> & {
  attendeeItems: AttendeeItem[]
}

export type CreateCheckoutResponse = {
  id: string
}

export type LocalStorageCartMeta = {
  outletId: string
  checkoutId: string
  isPreOrderCheckout?: boolean
}

export type LocalStorageCheckoutPaymentRedirect = {
  outletId: string
  checkoutId: string
}

export type CheckoutInvoicePaymentResponse = {
  paymentResult: PaymentProviderResult
  paymentId: string
}

export type YourInfoFormState = {
  firstName: string
  lastName: string
  phoneNumber: string
}

export type CheckoutStatusPageParams = {
  outletId?: string
  checkoutId?: string
}

export type CheckoutStatusPageQueryParams = CheckoutStatusPageParams & {
  /** Whether or not redirected from payment completion */
  redirect?: boolean
}

export type CheckoutResultPageQuery = CheckoutStatusPageQueryParams & {
  [k: string]: string
}

export type InvoicePaymentFormState = {
  invoiceReferenceNote: string
}

export type CheckoutUpdateError = {
  stockErrorAdditionalData?: CheckoutOutOfStockAdditionalData[]
  invalidDataErrorAdditionalData?: CheckoutInvalidDataErrorAdditionalData[]
  capacityConflictErrorPayload?: CapacityConflictErrorPayload
  /** The scheduled service time has passed and a new one must be picked */
  isServiceTimeInThePast?: boolean
  /** Core had canceled the checkout, so it was replaced with a new one */
  isCheckoutRecreated?: boolean
}

/** What createCheckout rejects with, besides the error modal it shows */
export type CheckoutCreateError = {
  /** Core refused the request itself (400), so retrying it won't help */
  isRequestRefused?: boolean
}

/** What recreateCanceledCheckout rejects with */
export type CheckoutRecreateError = {
  /** The canceled checkout was dropped and the customer starts over */
  isCheckoutReleased?: boolean
}

type CheckoutOrderSummaryItem = {
  ticketItem: {
    status: TicketItemStatus
  } | null
}

type CheckoutOrderSummary = {
  id: string
  orderFormat: OrderFormat
  items: Array<CheckoutOrderSummaryItem | CheckOrderSummaryItem>
  metaData?: CheckoutMetaDataItem[]
  deliveryStatus?: CheckoutDeliveryStatus | null
  deliveryProvider?: CheckoutDeliveryProvider | null
}

type PreOrderOptions = {
  isHost: boolean
  preOrderStatus: PreOrderStatus
  preOrderCancelInfo?: PreOrderCancelInfo | null
  preOrderAttendeeStatus: AttendeePreOrderStatus
}

export type OrderSummaryStatusState = {
  checkout: CheckoutOrderSummary | null
  preparationTime?: string
  serviceTime?: ServiceTime
  checkoutStatus?: CheckoutStatus
  customersFetchTableOrders: boolean
  enablePartiallyPrepareOrders: boolean
  ticketStatus?: TicketStatus
  preOrderOptions?: PreOrderOptions
  isCheckOrder?: boolean
}

export type CheckoutPaymentSettings = {
  enableClickToPay: boolean
}

export type OrderInstructionsFormState = {
  orderNote: string
}

export type OrderSummaryTotalsType = {
  subtotalInclTax: Amount
  minimumOrderDifferenceAmount: Amount
  feeAmountInclTax: Amount
  discount: CheckoutDiscount | null
  subTotalDiscountInclTax: Amount
  orderFormat: OrderFormat
  totalAmount: Amount
}

export type CheckoutAdyenParams = {
  outletId: string
  checkoutId: string
}

export type CheckoutInvalidDataErrorAdditionalData = {
  errorCode: string
  productName: string
  productId: string
  variantId: string
  variantType?: VariantType
}

export type CheckoutUnavailableProduct = IDNameItem & {
  variantType?: VariantType
}

// TODO: Move to wildcat-types - GET v1/orderStatus/{orderId} returns variantType
export type OrderSummaryOrderItemWithVariantType = OrderSummaryOrderItem & {
  product: OrderSummaryTicketItemProduct & { variantType?: VariantType }
}

export type AdyenCheckoutAdditionalData = {
  /** For Wallet top up */
  walletId?: string | null

  /** For Gift Card purchase */
  draftGiftCardId?: string | null
  externalPurchase?: boolean
}

export type CheckoutUnavailableOrderTimeErrorAdditionalData = {
  isPreOrderCheckout?: boolean
}

export type UnavailableOverrideMenuItems = {
  checkoutItemProductVariantIds: string[]
}

export enum CheckoutSystemProductWarningType {
  None,
  AddWalletTopUp = 'addWalletTopUp',
  AddGiftCard = 'addGiftCard',
  AddProductToWalletCheckout = 'addProductToWalletCheckout',
  AddProductToGiftCardCheckout = 'addProductToGiftCardCheckout',
  StartGiftCardPurchase = 'startGiftCardPurchase',
}

export enum CapacityConflictOptionType {
  ChangePickupTime = 'changePickupTime',
  AdjustOrder = 'adjustOrder',
  DeleteOrder = 'deleteOrder',
}

export enum CapacityConflictType {
  SomeProductsBlocked,
  AllProductsBlocked,
}

/** How the guest chose to resolve one short cart line. */
export enum CapacityConflictLineMode {
  /** Keep everything the kitchen can still make of this line. */
  Max = 'max',
  /** Keep a smaller number the guest picks with the stepper. */
  Reduce = 'reduce',
  /** Drop the line entirely. */
  Remove = 'remove',
}

/** A cart line as the capacity allocator sees it. */
export interface CapacityConflictLine {
  id: string
  variantId: string
  /** Null when the item's product could not be resolved from anything we hold. */
  productId: string | null
  name: string
  variantType?: VariantType
  quantity: number
}

/** The guest's overrides. Anything absent falls back to the suggested split. */
export interface CapacityConflictChoices {
  /** Keyed by line id. */
  lineModes: Record<string, CapacityConflictLineMode>
  /** Keyed by line id. */
  lineQuantities: Record<string, number>
}

/**
 * A capacity pool the kitchen is short on. The quantity is shared across every
 * product listed, so two different products in one group compete for it.
 */
export interface CapacityConflictSlotGroup {
  maximumAllowedQuantity: number
  productIds: string[]
}

export interface CapacityConflictPickupTimeRange {
  start: string
  end: string
}

/** Raw shape of the API error's additionalData, before parsing into modal state. */
export interface CapacityConflictRawAdditionalData {
  /** Keyed by slot group id. Only the groups that are actually blocking. */
  blockingSlotGroups: Record<string, CapacityConflictSlotGroup>
  availablePickupTimeRanges: CapacityConflictPickupTimeRange[]
}

/** Which API call was refused, so the modal knows how to re-submit. */
export type CapacityConflictOrigin = 'create' | 'update'

/** Everything the modal needs, gathered where the failing request was made. */
export interface CapacityConflictErrorPayload {
  additionalData: CapacityConflictRawAdditionalData
  /** The cart that was sent, so the guest's answer can be re-submitted. */
  requestedItems: CheckoutRequestItem[]
  /**
   * The pickup time that was refused. Captured at the call site because a rejected
   * update rolls the stored filters back to the server's time.
   */
  requestedTime: ServiceTime
  origin: CapacityConflictOrigin
  outletId: string
}

export interface CapacityConflictModalState extends CapacityConflictChoices {
  /** Selectable pickup times, expanded from the ranges the API returned. */
  availableTimes: string[]
  /**
   * Units the kitchen can still make, keyed by product id. A slot group's limit
   * applies to each of its products, so the groups are flattened away here.
   */
  productCapacities: Record<string, number>
  /**
   * The cart that was attempted, resolved once when the error arrived - the stored
   * checkout still holds the previous state after a rejection, so it can't be
   * derived later.
   */
  lines: CapacityConflictLine[]
  /** Exactly what was sent, kept so the guest's answer can be re-submitted. */
  requestedItems: CheckoutRequestItem[]
  /** The pickup time that was refused, for the modal copy. */
  requestedTime: ServiceTime
  origin: CapacityConflictOrigin
  outletId: string
  selectedOption: CapacityConflictOptionType | null
}

/**
 * The verb the step is really asking the guest to do, which drives the option
 * label, the step title and the primary CTA.
 */
export enum CapacityConflictAdjustVerb {
  Remove = 'remove',
  Update = 'update',
}

export interface CapacityConflictRowView {
  lineId: string
  name: string
  variantType?: VariantType
  orderedQuantity: number
  /** How many of the line survive. */
  keptQuantity: number
  /** Most the guest can raise this line to right now. */
  maxQuantity: number
  /** What the kitchen can still make of it, or null when unlimited. */
  availableCount: number | null
  mode: CapacityConflictLineMode
  /** True in the pooled group, where the guest owns the decision. */
  isAdjustable: boolean
  /**
   * Set when the pool this line draws on is entirely spoken for, so the guest has to
   * free capacity elsewhere before this row can take any.
   */
  blockedByOtherLines: {
    productName: string
    variantType?: VariantType
    capacity: number
  } | null
}

export enum CapacityConflictGroupKind {
  Choose = 'choose',
  Remove = 'remove',
}

export type GetCheckoutStatusTrackerOptions = {
  /** Company and group orders are confirmed by the restaurant first */
  isCompanyOrder?: boolean
  isDelivery?: boolean
}

export type CheckoutFlowCopy = {
  /** Heading of the review card */
  titleKey: string
  /** Label of the invoice pay button; card payments keep Adyen's own button */
  invoiceCtaKey: string
  /** Line under the pay box */
  reassuranceKey: string
}

/** An add to cart in flight */
export interface CheckoutAddItemRequest {
  /** The number of items being added */
  qty: number
  /** Core has the items, so details fetched from now on include them */
  isSaved: boolean
  /** The first details fetch started after the items were saved */
  fetchRequestId: string | null
  /** The current details include the items, so they no longer count extra */
  isInDetails: boolean
}

// TODO: Move to wildcat-types, `GET v1.2/Outlets/{id}` returns the outlet's online payment provider
export type OutletDetailsWithOnlinePaymentProvider = OutletDetails & {
  onlinePaymentProvider?: OnlinePaymentProvider | null
}

// TODO: Use CheckoutSurfboardPaymentPayload from wildcat-types 4.136.0
/** Payload of `POST v1.2/Outlets/{outletId}/Checkouts/{checkoutId}/Surfboard/Payments` */
export type CheckoutSurfboardPaymentPayload = {
  /** Optional, not sent for gift cards and wallet top-ups */
  tipAmount?: Amount
}

// TODO: Use CheckoutSurfboardPaymentResponse from wildcat-types 4.136.0
/** Response of `POST v1.2/Outlets/{outletId}/Checkouts/{checkoutId}/Surfboard/Payments` */
export type CheckoutSurfboardPaymentResponse = {
  /** `ActionRequired` means the customer pays in the Surfboard SDK next, and the checkout status reports the outcome */
  paymentResult: PaymentProviderResult
  paymentId: string
  paymentProviderData: {
    orderId: string
    nonce: string
    publicKey: string
    sdkUrl: string
  }
}
